import React from 'react';

import { Grid } from '@cowbell/ui';

import { Show } from '../../../Show';
import type { Languages } from '../../../../i18n/i18n.language-config';
import { OrgAddressFields } from './OrgAddressFields';
import { OrgPhoneField } from './OrgPhoneField';
import classes from './OrgBusinessAddressPanel.module.css';
import { OrgCompletionBadge } from './OrgCompletionBadge';
import { OrgPanel } from './OrgPanel';
import type { OrgAddressFormApi } from './useOrgAddressForm';

interface OrgBusinessAddressPanelProps {
  readonly form: OrgAddressFormApi;
}

/**
 * The Business Address panel: the phone number and the physical location.
 *
 * <p>Which fields appear, and what they are called, is the platform's country's
 * business -- `gridLayoutSettings` hides State outside the US and AU and adds a
 * third address line for the UK. The supplied design draws a US-only form with
 * a hardcoded state list; keeping `LanguageForm` here is what stops this page
 * breaking on the other platforms.
 */
export const OrgBusinessAddressPanel = ({
  form,
}: OrgBusinessAddressPanelProps) => {
  const { gridLayoutSettings } = form;

  return (
    <OrgPanel
      id="address"
      title="Business address"
      description="The physical location and phone number on record for this organization."
      status={<OrgCompletionBadge missing={form.businessMissingCount} />}
    >
      <Grid gutter="md">
        {/* The design gives the phone its own row -- Address 1 starts the next
            one rather than sitting beside it -- so this column is full width
            and the control keeps a field column's worth of it. */}
        <Show when={Boolean(gridLayoutSettings.showPhoneNumber)}>
          <Grid.Col span={12}>
            <div className={classes.phoneRow}>
              <OrgPhoneField label="Business phone" />
            </div>
          </Grid.Col>
        </Show>

        <OrgAddressFields
          language={form.language as Languages}
          layout={gridLayoutSettings}
        />
      </Grid>
    </OrgPanel>
  );
};

export default OrgBusinessAddressPanel;
